import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FaGoogle, FaFacebook, FaUser, FaEnvelope, FaMobile, FaLock } from 'react-icons/fa'
import { useAuth } from '../context/AuthContext'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import authService from '../services/authService'
import SEO from '../utils/SEO'
import { trackSignUp } from '../utils/analytics'
import Button from '../components/ui/Button'

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    password: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const { register } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    setLoading(true)

    try {
      const { confirmPassword, ...registerData } = formData
      await register(registerData)
      trackSignUp('email')
      toast.success('Registration successful!')

      // Check for redirect parameter
      const redirect = searchParams.get('redirect')
      if (redirect) {
        navigate(`/${redirect}`)
      } else {
        navigate('/')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = () => {
    authService.googleAuth()
  }

  const handleFacebookLogin = () => {
    authService.facebookAuth()
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEO title="Create Account" noindex />
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-display font-bold text-gradient">
            Join Himachal Yatra Today
          </h2>
          <p className="mt-2 text-gray-600">Create your account and start exploring</p>
        </div>

        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-8">
          {/* Social Registration */}
          <div className="space-y-3 mb-6">
            <Button
              variant="outline"
              fullWidth
              icon={<FaGoogle className="text-red-500" size={20} />}
              onClick={handleGoogleLogin}
            >
              Sign up with Google
            </Button>
            <Button fullWidth icon={<FaFacebook size={20} />} onClick={handleFacebookLogin}>
              Sign up with Facebook
            </Button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or register with</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">
                <FaUser className="inline mr-2" />
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="label">
                <FaEnvelope className="inline mr-2" />
                Email (Optional)
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className="input-field"
              />
            </div>

            <div>
              <label className="label">
                <FaMobile className="inline mr-2" />
                Mobile Number
              </label>
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                placeholder="+91 XXXXX XXXXX"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="label">
                <FaLock className="inline mr-2" />
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="label">
                <FaLock className="inline mr-2" />
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="input-field"
                required
              />
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1"
                required
              />
              <span className="ml-2 text-sm text-gray-600">
                I agree to the{' '}
                <Link
                  to="/terms-and-conditions"
                  className="text-primary-600 hover:text-primary-700"
                >
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-700">
                  Privacy Policy
                </Link>
              </span>
            </div>

            <Button type="submit" fullWidth disabled={loading} loading={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link
                to={
                  searchParams.get('redirect')
                    ? `/login?redirect=${searchParams.get('redirect')}`
                    : '/login'
                }
                className="link-primary"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Register
